/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

// @ts-nocheck

import { createAsyncThunk } from '@reduxjs/toolkit';

import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { ApiStatus } from '../../../ts';
import { AIModel } from '../../../ts/aiModels';
import { GetDataSourceResponse, MilvusIndices } from '../../../ts/milvus';
import type { CreateAsyncThunk } from '../../../ts/state';
import { getBaseLLMModelData } from '../../actions/api/actions';
import * as milvusApi from '../api';
import {
  selectDraftUrl,
  selectDraftPort,
  selectDraftUser,
  selectDraftPass,
  selectDraftDatabase
} from './selectors';

export const checkCreds = createAsyncThunk<
  string,
  { assistantId: string },
  CreateAsyncThunk
>(
  'milvus/checkCreds',
  async (params, { dispatch, getState }) => {
    const { assistantId } = params;
    const url = selectDraftUrl(getState());
    const port = selectDraftPort(getState());
    const username = selectDraftUser(getState());
    const password = selectDraftPass(getState());

    try {
      const response = await milvusApi.checkCredentials(
        assistantId,
        url,
        username,
        password,
        port
      );

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { checkCredStatus } = getState()?.milvus;
      if (checkCredStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const listDataSources = createAsyncThunk<
  GetDataSourceResponse,
  { skillId: string; assistantId: string },
  CreateAsyncThunk
>(
  'milvus/listDataSources',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId } = params;
    const url = selectDraftUrl(getState());
    const port = selectDraftPort(getState());
    const username = selectDraftUser(getState());
    const password = selectDraftPass(getState());

    try {
      const response = await milvusApi.listDataSources(
        skillId,
        assistantId,
        url,
        username,
        password,
        port
      );

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { listDatabasesStatus } = getState()?.milvus;
      if (listDatabasesStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);
export const listCollections = createAsyncThunk<
  GetDataSourceResponse,
  { skillId: string; assistantId: string; database: string },
  CreateAsyncThunk
>(
  'milvus/listCollections',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId, database } = params;

    const url = selectDraftUrl(getState());
    const port = selectDraftPort(getState());
    const username = selectDraftUser(getState());
    const password = selectDraftPass(getState());

    try {
      const response = await milvusApi.listCollections(
        skillId,
        assistantId,
        url,
        database,
        username,
        password,
        port
      );

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { listCollectionsStatus } = getState()?.milvus;
      if (listCollectionsStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const getIndices = createAsyncThunk<
  MilvusIndices,
  {
    skillId: string;
    assistantId: string;
    collectionName: string;
  },
  CreateAsyncThunk
>(
  'milvus/getIndices',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId, collectionName } = params;
    const url = selectDraftUrl(getState());
    const port = selectDraftPort(getState());
    const username = selectDraftUser(getState());
    const password = selectDraftPass(getState());
    const database = selectDraftDatabase(getState());
    try {
      const response = await milvusApi.getIndices(
        skillId,
        assistantId,
        url,
        database,
        collectionName,
        username,
        password,
        port
      );

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { getIndexStatus } = getState()?.milvus;
      if (getIndexStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);
export const getFields = createAsyncThunk<
  MilvusIndices,
  { skillId: string; assistantId: string; collectionName: string },
  CreateAsyncThunk
>(
  'milvus/getFields',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId, collectionName } = params;
    const url = selectDraftUrl(getState());
    const port = selectDraftPort(getState());
    const username = selectDraftUser(getState());
    const password = selectDraftPass(getState());
    const database = selectDraftDatabase(getState());

    try {
      const response = await milvusApi.getFields(
        skillId,
        assistantId,
        url,
        database,
        collectionName,
        username,
        password,
        port
      );

      return response;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { getFieldsStatus } = getState()?.milvus;
      if (getFieldsStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const getModels = createAsyncThunk<AIModel[], void, CreateAsyncThunk>(
  'milvus/getModels',
  async (params, { dispatch }) => {
    try {
      const response = await getBaseLLMModelData('embeddings');

      return response?.data?.models;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { getModelsStatus } = getState()?.milvus;
      if (getModelsStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);
